import {useSyncExternalStore} from 'react'

/**
 * Which experience a user with the `ui_v2_experience` flag has chosen in this browser
 * (AB#6639): the new one (the default) or the classic one. Shared with the v2
 * app through the same storage key and change event (its
 * `src/v2/core/preferences/navigationExperience.ts`): a contract, not shared
 * code. A user without the flag never sees the choice.
 */
export type NavigationExperience = 'new' | 'classic'

const STORAGE_KEY = 'mbai-navigation-experience'
const CHANGE_EVENT = 'mbai:navigation-experience'

const read = (): NavigationExperience => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'classic' ? 'classic' : 'new'
  } catch {
    return 'new'
  }
}

const subscribe = (onChange: () => void): (() => void) => {
  window.addEventListener(CHANGE_EVENT, onChange)
  // Another tab switching changes this one too.
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

export const setNavigationExperience = (next: NavigationExperience): void => {
  try {
    if (next === 'classic') {
      window.localStorage.setItem(STORAGE_KEY, 'classic')
    } else {
      window.localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Not remembered; the choice still applies until the page reloads.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

export const useNavigationExperience = (): NavigationExperience =>
  useSyncExternalStore(subscribe, read, () => 'new')
